import * as React from 'react'

import { Text } from '@factorialco/deprecated-design-system'
import classNames from 'classnames/bind'
import type { Field } from 'factorial-form'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import BrowserBarPreview, { normalizeSubdomain } from 'modules/core/components/BrowserBarPreview'
import type { SizeType } from 'modules/core/components/BrowserBarPreview/BrowserImage'
import FormRow from 'modules/core/components/FormRow'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'
import Constant from 'modules/core/store/models/Constant'

import { companyMainLocation } from 'modules/locations/store/collections/locations'

import styles from './index.module.css'

interface Props {
  slugField: Field
  domainField: Field
  label?: string
  placeholder?: string
  information?: string
  size: SizeType
}

const cx = classNames.bind(styles)

const invalidStart = (s: string): boolean =>
  s.startsWith('www') || s.startsWith('http') || s.startsWith('https')

const invalidEnd = (s: string): boolean => s.endsWith('-es') || s.endsWith('-com')

/**
 * We want users to understand they don't need to
 * put `http://` or `www` or `-es/-com` at the end.
 */
export const validateNotSite = (field: Field): void => {
  const value = normalizeSubdomain(field.value)
  const invalid = invalidStart(value) || invalidEnd(value) || value.includes('.')

  if (invalid) {
    field.setErrors([i18n.t('subdomain_www_not_necessary')])
  } else {
    field.setErrors(null)
  }
}

const countryDomainOptions = new Constant({
  id: 'country_domain_options',
})

const Subdomain = ({
  slugField,
  domainField,
  size,
  label = i18n.t('settings.edit_company.company_subdomain'),
  placeholder = i18n.t('settings.edit_company.company_subdomain_placeholder'),
  information,
}: Props) => {
  const [loading, setLoading] = React.useState(true)
  const [focused, setFocused] = React.useState(false)
  React.useEffect(() => {
    const loadDomains = async () => {
      await countryDomainOptions.fetch()
      setLoading(false)
    }

    loadDomains()
  }, [])

  const domains = () => {
    const domainsList = countryDomainOptions.toJS().value
    const country = companyMainLocation().attributes.country

    const sortedList = sortBy(domainsList, function (item) {
      return item.includes(country) ? 0 : 1
    })

    return sortedList
  }

  if (loading) return <Loading />

  return (
    <>
      <FormRow>
        <div
          className={cx('domainTextBox', {
            focused,
          })}
          tabIndex={0}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        >
          <label className={styles.label}>
            <Text.Normal ellipsis size="200" weight="semibold" color="grey600">
              {label}
            </Text.Normal>
          </label>
          <div className={styles.icon}>
            {information && <InformationTooltip information={information} />}
          </div>
          <div className={styles.content}>
            <input
              value={slugField.value}
              onChange={(event) => slugField.set(event.target.value)}
              placeholder={placeholder}
              className={styles.text}
            />
            <select
              value={domainField.value}
              onChange={(event) => domainField.set(event.target.value)}
              className={styles.dropdown}
            >
              {domains().map((domain, i) => (
                <option className={styles.option} key={i} value={domain}>
                  {domain}
                </option>
              ))}
            </select>
          </div>
        </div>
      </FormRow>
      <BrowserBarPreview
        size={size}
        subdomain={slugField.value}
        domain={domainField.value ? domainField.value : domains()[0]}
      />
    </>
  )
}

export default observer(Subdomain)
